import { useState, useCallback, memo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip, Button, Select, Spin } from 'antd';
import { InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import WebIcon from 'src/_shared/components/WebIconComponent.jsx';
import { brightenColor } from 'src/customizable-ui/dashboard/utils/brightenColor.js';
import { useApolloClient, gql } from '@apollo/client';
import {
	getISODateRangeUTC,
	parse,
	searchOpenSearchNetwork,
	subtractDays,
} from 'src/_shared/services/utils.js';
import { updateUser as updateUserMutation } from 'src/_shared/api/graphql/custom/users/updateUserGraphql.js';

const { Option } = Select;

const JobAlertsCard = memo(
	({
		card,
		onUpdate,
		isLocked,
		isEditMode = true,
		isDragging = false,
		isResizing = false,
		isHovered = false,
		currentUser,
		setCurrentUser,
		departments = [],
		subCompanies = [],
		jobNotificationSetting = {},
		hideSubCompany = false,
	}) => {
		const apolloClient = useApolloClient();
		const isPayRateHidden =
			parse(currentUser?.company?.jobFilterSettings ?? '{}')?.payRate?.hidden ??
			false;
		const timerRef = useRef(null);
		const [editingField, setEditingField] = useState(null);
		const [settingsView, setSettingsView] = useState(false);
		const [loading, setLoading] = useState(true);
		const [searchedJobs, setSearchedJobs] = useState([]);
		const [departmentOptions, setDepartmentOptions] = useState([]);
		const [subCompanyOptions, setSubCompanyOptions] = useState([]);
		const [selectedDepartmentOptions, setSelectedDepartmentOptions] = useState(
			[]
		);
		const [selectedSubCompanyOptions, setSelectedSubCompanyOptions] = useState(
			[]
		);
		const [selectedSalaryOption, setSelectedSalaryOption] = useState(undefined);
		const [editValues, setEditValues] = useState({
			title: card.data?.title || '',
			subtitle: card.data?.subtitle || '',
			buttonText: card.data?.buttonText || '',
		});

		useEffect(() => {
			const sortedDepartments = departments
				.filter(
					(department) => department.active && department.name.trim() !== ''
				)
				.sort((a, b) => a.name.localeCompare(b.name));

			const deptOptions = sortedDepartments.map((department) => (
				<Option key={department.id} value={department.id}>
					{department.name}
				</Option>
			));

			const sortedSubCompanies = [...subCompanies].sort((a, b) =>
				a.name.localeCompare(b.name)
			);

			const subCompOptions = sortedSubCompanies.map((subCompany) => (
				<Option key={subCompany.id} value={subCompany.id}>
					{subCompany.name}
				</Option>
			));

			setDepartmentOptions(deptOptions);
			setSubCompanyOptions(subCompOptions);

			const selectedDept = jobNotificationSetting?.departmentSelection || [];
			const selectedSubComp =
				jobNotificationSetting?.subcompanySelection && !hideSubCompany
					? jobNotificationSetting.subcompanySelection
					: [];
			const selectedSal =
				jobNotificationSetting?.salarySelection > 0 ||
				jobNotificationSetting?.salarySelection === 0
					? jobNotificationSetting.salarySelection
					: undefined;

			setSelectedDepartmentOptions(selectedDept);
			setSelectedSubCompanyOptions(selectedSubComp);
			setSelectedSalaryOption(selectedSal);

			const newState = {
				selectedDepartmentOptions: selectedDept,
				selectedSubCompanyOptions: selectedSubComp,
				selectedSalaryOption: selectedSal,
			};
			setQueryToState('', 0, newState);
		}, [departments, subCompanies, jobNotificationSetting, hideSubCompany]);

		useEffect(() => {
			setEditValues({
				title: card.data?.title || '',
				subtitle: card.data?.subtitle || '',
				buttonText: card.data?.buttonText || '',
			});
			if (isEditMode) {
				setSettingsView(false);
			}
		}, [
			card.data?.title,
			card.data?.subtitle,
			card.data?.buttonText,
			isEditMode,
		]);

		const setQueryToState = useCallback(
			async (searchQuery = '', timeout = 0, overrideState = null) => {
				const selectedDept = overrideState?.selectedDepartmentOptions
					? overrideState.selectedDepartmentOptions
					: selectedDepartmentOptions;
				const selectedSubComp = overrideState?.selectedSubCompanyOptions
					? overrideState.selectedSubCompanyOptions
					: selectedSubCompanyOptions;
				const selectedSal = overrideState?.selectedSalaryOption
					? overrideState.selectedSalaryOption
					: selectedSalaryOption;

				const filteredDepartments = selectedDept.map((options) => options.key);
				const filteredSubCompanies = selectedSubComp.map(
					(options) => options.key
				);
				const salary = selectedSal;

				const companies = [currentUser?.company?.id];
				const filteredCompanies = companies;

				clearTimeout(timerRef.current);
				timerRef.current = setTimeout(async () => {
					setLoading(true);
					const [from, to] = getISODateRangeUTC([
						subtractDays(14),
						new Date().toISOString(),
					]);
					const parameters = {
						query: searchQuery,
						size: 300,
						role: currentUser?.role,
						currentUser: currentUser?.id,
						filters: {
							companies: filteredCompanies,
							departments: filteredDepartments,
							status: 'open',
							subCompanies: filteredSubCompanies,
							dateCreated: {
								from,
								to,
							},
						},
					};
					if (salary > 0 || salary === 0) parameters.filters.salary = salary;

					try {
						const response = await searchOpenSearchNetwork(
							parameters,
							'erin-jobs'
						);
						if (response?.data) {
							setSearchedJobs(response.data);
						}
						setLoading(false);
					} catch (error) {
						console.error('Error searching jobs:', error);
						setLoading(false);
					}
				}, timeout);
			},
			[
				selectedDepartmentOptions,
				selectedSubCompanyOptions,
				selectedSalaryOption,
				currentUser,
			]
		);

		const handleSelectDepartment = (option, data) => {
			const departments = [];
			for (const item of data) {
				const department = {
					key: item.props.value,
					label: item.props.children,
				};
				departments.push(department);
			}

			setSelectedDepartmentOptions(departments);
		};

		const handleSelectSubCompany = (option, data) => {
			const subCompanies = [];
			for (const item of data) {
				const subCompany = {
					key: item.props.value,
					label: item.props.children,
				};
				subCompanies.push(subCompany);
			}

			setSelectedSubCompanyOptions(subCompanies);
		};

		const handleSelectSalary = (option) => {
			setSelectedSalaryOption(option);
		};

		const savePreferences = async () => {
			let jobNotificationSettingObj = {
				departmentSelection: selectedDepartmentOptions,
				subcompanySelection: selectedSubCompanyOptions,
			};
			if (isPayRateHidden) {
				// Min. Pay Rate filter is hidden for this company; default to
				// "Any Min. Pay Rate" (0) since the control isn't shown.
				jobNotificationSettingObj.salarySelection = 0;
			} else if (selectedSalaryOption > 0 || selectedSalaryOption === 0) {
				jobNotificationSettingObj.salarySelection = selectedSalaryOption;
			}
			const jobNotificationSettingStr = JSON.stringify(
				jobNotificationSettingObj
			);

			const updatedUser = {
				...currentUser,
				jobNotificationSetting: jobNotificationSettingStr,
			};
			setCurrentUser(updatedUser);

			try {
				await apolloClient.mutate({
					mutation: gql(updateUserMutation),
					variables: {
						input: {
							id: currentUser?.id,
							jobNotificationSetting: jobNotificationSettingStr,
						},
					},
				});

				await setQueryToState('');

				setSettingsView(false);
			} catch (error) {
				console.error('Error saving preferences:', error);
			}
		};

		const renderArrowIcon = () => {
			return (
				<div>
					<WebIcon color="var(--oslo-gray)" size={10} name="sort-down" />
				</div>
			);
		};

		const handleStartEdit = useCallback(
			(field, e) => {
				e?.stopPropagation();
				if (isLocked || !isEditMode) return;
				setEditingField(field);
			},
			[isLocked, isEditMode]
		);

		const handleFinishEdit = useCallback(() => {
			setEditingField(null);
			if (onUpdate && editValues[editingField]) {
				onUpdate({
					...card.data,
					[editingField]: editValues[editingField],
				});
			}
		}, [editingField, editValues, card.data, onUpdate]);

		const handleChange = useCallback((field, value) => {
			setEditValues((prev) => ({ ...prev, [field]: value }));
		}, []);

		const handleKeyDown = useCallback(
			(e) => {
				if (e.key === 'Enter' && !e.shiftKey) {
					e.preventDefault();
					handleFinishEdit();
				} else if (e.key === 'Escape') {
					setEditingField(null);
					setEditValues({
						title: card.data?.title || '',
						subtitle: card.data?.subtitle || '',
						buttonText: card.data?.buttonText || '',
					});
				}
			},
			[handleFinishEdit, card.data]
		);

		const cardData = card.data || {};
		const backgroundColor = cardData.backgroundColor || '#3b82f6';
		const borderColor = cardData.borderColor || '#2563eb';
		const textColor = cardData.textColor || '#ffffff';
		const countColor = cardData.countColor || '#000000';
		const showBorder = cardData.showBorder !== false;
		const borderSize = cardData.borderSize || 2;
		const tooltipText =
			cardData.tooltipText ||
			'These are new job matches from the last 14 days that match your criteria.';
		const buttonBackground = cardData.buttonBackground || '#ffffff';
		const buttonTextColor = cardData.buttonTextColor || backgroundColor;
		const link = cardData.link || '/dashboard';

		const getCardBorderStyle = () => {
			if (!showBorder && !isEditMode) return 'none';

			if (isDragging || isResizing) {
				return `${borderSize}px solid #74a9ffff`;
			}

			if (isEditMode && isHovered && !isDragging && !isResizing) {
				const brighterBorder = brightenColor(borderColor || '#2563eb', 30);
				return `${borderSize}px solid ${brighterBorder}`;
			}

			if (showBorder) {
				return `${borderSize}px solid ${borderColor}`;
			}

			return 'none';
		};

		const getCardShadow = () => {
			if (isDragging || isResizing) {
				return '0 25px 50px -12px rgba(0, 0, 0, 0.25)';
			}
			if (isEditMode && isHovered && !isDragging && !isResizing) {
				return '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
			}
			return 'none';
		};

		const renderEditableText = (
			field,
			className,
			placeholder,
			customStyles = {}
		) => {
			const isEditing = editingField === field;
			const displayValue = cardData[field] || placeholder;
			const editValue = editValues[field] || displayValue;

			const mergedStyles = {
				color: field === 'count' ? countColor : textColor,
				...customStyles,
				maxWidth: '100%',
			};

			if (isEditing && field !== 'count') {
				const textLength = editValue.length || placeholder.length;
				const dynamicWidth = `${Math.min(Math.max(textLength * 13, 13), 95)}%`;

				return (
					<input
						type="text"
						value={editValue}
						onChange={(e) => handleChange(field, e.target.value)}
						onBlur={handleFinishEdit}
						onKeyDown={handleKeyDown}
						className={`${className} bg-transparent border-2 border-white border-dashed rounded px-2 py-1 focus:outline-none focus:border-opacity-100`}
						style={{
							...mergedStyles,
							width: dynamicWidth,
							maxWidth: '95%',
							minWidth: '100px',
						}}
						autoFocus
						onClick={(e) => e.stopPropagation()}
						data-no-drag
					/>
				);
			}

			return (
				<div
					className={`${className} ${!isLocked && isEditMode ? 'cursor-text hover:opacity-80 transition-opacity' : ''}`}
					onClick={(e) => {
						if (isEditMode) {
							e.stopPropagation();
							handleStartEdit(field, e);
						}
					}}
					onPointerDown={(e) => {
						if (isEditMode) e.stopPropagation();
					}}
					style={mergedStyles}
					data-no-drag={isEditMode ? true : undefined}
				>
					{displayValue}
				</div>
			);
		};

		const countStyles = {
			fontSize: 'clamp(1.1rem, 1.75rem + 2vw, 60px)',
			fontFamily: 'Nunito Sans, sans-serif',
			fontWeight: 800,
			lineHeight: 0.9,
			marginBottom: '0',
			wordBreak: 'break-word',
		};

		const titleStyles = {
			fontSize: 'clamp(1rem, .75vw + .5rem, 18px)',
			fontFamily: 'Nunito Sans, sans-serif',
			fontWeight: 700,
			lineHeight: 1,
			marginBottom: 0,
			wordBreak: 'break-word',
		};

		const subtitleStyles = {
			fontSize: 'clamp(.8rem, .75vw + .5rem, 12px)',
			fontFamily: 'Nunito Sans, sans-serif',
			fontWeight: 500,
			lineHeight: 1,
			marginBottom: '10px',
			wordBreak: 'break-word',
		};

		const buttonStyles = {
			fontSize: 'clamp(.75rem, 0.75rem + 0.5vw, 16px)',
			textTransform: 'uppercase',
			fontFamily: '"Nunito Sans", sans-serif',
			fontWeight: 700,
			lineHeight: 1,
			borderRadius: '20px',
			padding: '12px 20px',
			textAlign: 'center',
		};

		return (
			<div
				className="w-full h-full flex flex-col items-center justify-center relative transition-all duration-200"
				style={{
					background: !isEditMode && settingsView ? '#ffffff' : backgroundColor,
					border: !isEditMode && settingsView ? 'none' : getCardBorderStyle(),
					boxShadow: getCardShadow(),
					transform: isDragging ? 'scale(1.05)' : 'scale(1)',
					borderRadius: '14px',
					gap: '8px',
					padding: !isEditMode && settingsView ? 0 : '16px',
				}}
			>
				{!settingsView ? (
					<div
						className="w-full h-full flex flex-col items-center justify-center relative transition-all duration-200"
						style={{
							background: backgroundColor,
							gap: '8px',
							padding: '16px',
						}}
					>
						{loading ? (
							<div
								style={{
									...countStyles,
									color: countColor,
								}}
							>
								<Spin
									indicator={
										<LoadingOutlined
											spin
											style={{ fontSize: 36, color: countColor }}
										/>
									}
								/>
							</div>
						) : (
							renderEditableText(
								'count',
								'text-2xl font-bold text-center',
								searchedJobs.length.toString(),
								countStyles
							)
						)}
						{renderEditableText(
							'title',
							'text-2xl font-bold text-center',
							'Title',
							titleStyles
						)}
						<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
							{renderEditableText(
								'subtitle',
								'text-base text-center opacity-90',
								'Subtitle',
								subtitleStyles
							)}
							<Tooltip
								placement="top"
								title={tooltipText}
								style={{ fontWeight: 1000 }}
							>
								<InfoCircleOutlined
									style={{
										cursor: 'pointer',
										//verticalAlign: '0.1em',
										color: 'var(--theme-primary)',
										marginBottom: '10px',
									}}
								/>
							</Tooltip>
						</div>

						{editingField === 'buttonText' ? (
							<div
								className="inline-block"
								style={{
									backgroundColor: buttonBackground,
									color: buttonTextColor,
									...buttonStyles,
									marginTop: '-12px',
									marginBottom: '-12px',
								}}
								data-no-drag
							>
								<input
									type="text"
									value={editValues.buttonText}
									onChange={(e) => handleChange('buttonText', e.target.value)}
									onBlur={handleFinishEdit}
									onKeyDown={handleKeyDown}
									className="bg-transparent border-2 border-blue-300 border-dashed rounded px-2 py-1 focus:outline-none text-center"
									style={{
										color: buttonTextColor,
										width: `${Math.max((editValues.buttonText || 'Button').length * 0.7, 6)}em`,
										maxWidth: '90%',
										minWidth: '100px',
									}}
									autoFocus
									onClick={(e) => {
										e.stopPropagation();
									}}
									onPointerDown={(e) => {
										e.stopPropagation();
									}}
									onMouseDown={(e) => {
										e.stopPropagation();
									}}
									data-no-drag
								/>
							</div>
						) : (
							<Link
								to={isEditMode ? '#' : link}
								className="inline-block hover:scale-105 hover:shadow-lg"
								style={{
									backgroundColor: buttonBackground,
									color: buttonTextColor,
									...buttonStyles,
									transition: 'all 0.3s ease-in-out',
								}}
								onClick={(e) => {
									if (isEditMode) {
										e.preventDefault();
										e.stopPropagation();
										handleStartEdit('buttonText', e);
										return false;
									}
								}}
								data-no-drag={isEditMode ? true : undefined}
							>
								<div
									className={
										!isLocked && isEditMode
											? 'cursor-text hover:opacity-80 transition-opacity'
											: ''
									}
								>
									{'VIEW JOBS'}
								</div>
							</Link>
						)}
						<button
							onClick={() => {
								if (isLocked || isEditMode) return;
								setSettingsView(true);
							}}
							style={{
								color: '#fff',
								fontSize: '12px',
								lineHeight: '14px',
								margin: '0',
								outline: 'none',
								border: 'none',
								background: 'transparent',
								cursor: 'pointer',
								textDecoration: 'none',
							}}
							onMouseEnter={(e) => {
								e.currentTarget.style.color = '#c9c9c9ff';
							}}
							onMouseLeave={(e) => {
								e.currentTarget.style.color = '#ffffff';
							}}
							data-no-drag
						>
							Edit Job Alerts
						</button>
					</div>
				) : (
					!isEditMode && (
						<div
							className="d-card alert-card height-equal"
							style={{
								background: '#ffffff',
								width: '100%',
								height: '100%',
								borderRadius: '14px',
								padding: '16px',
								overflowY: 'auto',
								margin: 0,
							}}
						>
							<div className="d-card-head" style={{ marginBottom: '10px' }}>
								<h4 className="d-title">Your Job Alerts</h4>
							</div>
							<div className="d-card-body">
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										marginBottom: 8,
										flexDirection: 'row',
										gap: '1rem',
									}}
								>
									<div
										className="custom-form-group"
										style={{ width: '50%', marginBottom: 0 }}
									>
										<label className="custom-label" style={{ marginBottom: 6 }}>
											Department:
										</label>

										<Select
											defaultValue={selectedDepartmentOptions.map(
												(options) => options.key
											)}
											mode="multiple"
											placeholder="Select"
											suffixIcon={renderArrowIcon()}
											style={{ borderRadius: 30, width: '100%' }}
											onChange={handleSelectDepartment}
										>
											{departmentOptions}
										</Select>
									</div>
									{!hideSubCompany && (
										<div
											className="custom-form-group"
											style={{ width: '50%', marginBottom: 0 }}
										>
											<label
												className="custom-label"
												style={{ marginBottom: 6 }}
											>
												Sub Company:
											</label>

											<Select
												defaultValue={selectedSubCompanyOptions.map(
													(options) => options.key
												)}
												mode="multiple"
												placeholder="Select"
												suffixIcon={renderArrowIcon()}
												style={{ borderRadius: 30, width: '100%' }}
												onChange={handleSelectSubCompany}
											>
												{subCompanyOptions}
											</Select>
										</div>
									)}
								</div>
								{!isPayRateHidden && (
									<div
										className="cutom-form-group"
										style={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											marginBottom: 10,
										}}
									>
										<label
											className="custom-label"
											style={{
												lineHeight: 1,
												margin: 0,
												width: '25%',
												color: '#000000',
											}}
										>
											Min. Pay Rate:
										</label>

										<Select
											placeholder="Select"
											defaultValue={selectedSalaryOption}
											suffixIcon={renderArrowIcon()}
											style={{ width: '75%' }}
											onChange={handleSelectSalary}
										>
											<Option key={1} value="">
												<span style={{ color: '#BFBFBF' }}>Select</span>
											</Option>
											<Option key={0} value={0}>
												Any Min. Pay Rate
											</Option>
											<Option key={30_000} value={30_000}>
												$15/hr. / $30,000/yr.
											</Option>
											<Option key={40_000} value={40_000}>
												$20/hr. / $40,000/yr.
											</Option>
											<Option key={50_000} value={50_000}>
												$25/hr. / $50,000/yr.
											</Option>
											<Option key={60_000} value={60_000}>
												$30/hr. / $60,000/yr.
											</Option>
											<Option key={70_000} value={70_000}>
												$35/hr. / $70,000/yr.
											</Option>
											<Option key={80_000} value={80_000}>
												$40/hr. / $80,000/yr.
											</Option>
											<Option key={90_000} value={90_000}>
												$45/hr. / $90,000/yr.
											</Option>
											<Option key={100_000} value={100_000}>
												$50/hr. / $100,000/yr.
											</Option>
											<Option key={110_000} value={110_000}>
												$55/hr. / $110,000/yr.
											</Option>
											<Option key={120_000} value={120_000}>
												$60/hr. / $120,000/yr.
											</Option>
											<Option key={140_000} value={140_000}>
												$70/hr. / $140,000/yr.
											</Option>
											<Option key={160_000} value={160_000}>
												$80/hr. / $160,000/yr.
											</Option>
											<Option key={180_000} value={180_000}>
												$90/hr. / $180,000/yr.
											</Option>
											<Option key={200_000} value={200_000}>
												$100/hr. / $200,000/yr.
											</Option>
											<Option key={250_000} value={250_000}>
												$125/hr. / $250,000/yr.
											</Option>
											<Option key={300_000} value={300_000}>
												$150/hr. / $300,000/yr.
											</Option>
										</Select>
									</div>
								)}
								<p className="text-center" style={{ fontSize: 10 }}>
									Email alerts are sent weekly. You can edit this under{' '}
									<a href="/myprofile">My Profile</a>
								</p>
								<Button
									block
									disabled={false}
									type="primary"
									shape="round"
									size="large"
									onClick={savePreferences}
								>
									Save Preferences
								</Button>
							</div>
						</div>
					)
				)}
			</div>
		);
	}
);

JobAlertsCard.displayName = 'JobAlertsCard';

export default JobAlertsCard;
